import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../css/BioassayComponent.css';
import { API_BASE_URL } from "../../config"; 
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';

const BioassayComponent = () => {
  const [bioassays, setBioassays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBioassay, setSelectedBioassay] = useState(null);
  const navigate = useNavigate();

  const fetchBioassays = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`http://197.156.76.122/api/bioassays/`);
      setBioassays(Array.isArray(response.data) ? response.data : response.data.results || []);
    } catch (e) {
      console.error('Failed to fetch bioassays', e);
      setBioassays([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchBioassays();
  }, []);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const openBioassay = (bio) => {
    setSelectedBioassay(bio);
    window.scrollTo(0, 0);
  };

  const closeBioassay = () => {
    setSelectedBioassay(null);
  };

  const extractReadableText = (data) => {
    if (!data) return 'N/A';
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch { return data; }
    }
    try {
      const info = data.Information;
      if (!Array.isArray(info)) return 'N/A';
      return info
        .flatMap(item => item?.Value?.StringWithMarkup?.map(s => s.String) || [])
        .filter(text => text && text.trim() !== '')
        .join('\n\n');
    } catch (err) {
      console.error('Parse error:', err);
      return 'N/A';
    }
  };

  const renderPMIDLinks = (pmids) => {
    if (!pmids) return 'N/A';
    const list = Array.isArray(pmids) ? pmids : [pmids];
    return list.map((pmid, i) => (
      <span key={i}>
        <a href={`https://pubmed.ncbi.nlm.nih.gov/${pmid}/`} target="_blank" rel="noopener noreferrer">{pmid}</a>
        {i < list.length - 1 && ', '}
      </span>
    ));
  };

  const renderDOILinks = (dois) => {
    if (!dois) return 'N/A';
    const list = Array.isArray(dois) ? dois : [dois];
    return list.map((doi, i) => (
      <span key={i}>
        <a href={`https://doi.org/${doi}`} target="_blank" rel="noopener noreferrer">{doi}</a>
        {i < list.length - 1 && ', '}
      </span>
    ));
  };

  /* =========================
     FULL PAGE VIEW
  ========================= */
  if (selectedBioassay) {
    const bio = selectedBioassay;

    return (
      <div className="bioassay-fullpage">
        {/* 🔙 Back to Home */}
        <button className="modal-back-home" onClick={() => navigate('/')}>
          <FaArrowLeft /> Back to Home
        </button>

        {/* Back to List */}
        <button className="modal-close-full" onClick={closeBioassay}>
          ← Back to List
        </button>

        <h1>{bio.aidname || 'Untitled BioAssay'}</h1>

        <p><strong>AID:</strong> {bio.aid}</p>
        <p><strong>Type:</strong> {bio.aidtypeid || 'N/A'}</p>

        <p><strong>Description:</strong></p>
        <pre className="bioassay-text">{extractReadableText(bio.aid_desc)}</pre>

        <p><strong>Comments:</strong></p>
        <pre className="bioassay-text">{extractReadableText(bio.aid_comment)}</pre>

        <p><strong>PMIDs:</strong> {renderPMIDLinks(bio.pmids)}</p>
        <p><strong>DOIs:</strong> {renderDOILinks(bio.dois)}</p>

        <p><strong>Data Source:</strong> {bio.aidsrcname || 'N/A'}</p>
        <p><strong>External ID:</strong> {bio.aidextid || 'N/A'}</p>
        <p><strong>Deposit Date:</strong> {formatDate(bio.depdate)}</p>
        <p><strong>Last Modified:</strong> {formatDate(bio.aidmdate)}</p>

        <h3>Related Links</h3>
        <ul>
          <li>
            <a href={`https://pubchem.ncbi.nlm.nih.gov/bioassay/${bio.aid}`} target="_blank" rel="noopener noreferrer">
              PubChem BioAssay
            </a>
          </li>
          {bio.aidextid && (
            <li>
              <a href={`https://www.ebi.ac.uk/chembl/assay_report_card/${bio.aidextid}/`} target="_blank" rel="noopener noreferrer">
                ChEMBL Assay
              </a>
            </li>
          )}
        </ul>
      </div>
    );
  }

  /* =========================
     LIST VIEW
  ========================= */
  return (
    <div className="bioassay-container">
      {/* 🔙 Back to Home */}
      <button className="modal-back-home" onClick={() => navigate('/')}>
        <FaArrowLeft /> Back to Home
      </button>

      <h2>BioAssays List</h2>

      {loading && <p>Loading bioassays…</p>}
      {!loading && bioassays.length === 0 && <p>No bioassay records found.</p>}

      {bioassays.map(bio => (
        <div key={bio.aid} className="bioassay-card">
          <h3>
            <span className="bioassay-title" onClick={() => openBioassay(bio)}>
              {bio.aidname || 'Untitled BioAssay'}
            </span>
          </h3>

          <p>
            <strong>BioAssay AID:</strong>{' '}
            <a href={`https://pubchem.ncbi.nlm.nih.gov/bioassay/${bio.aid}`} target="_blank" rel="noopener noreferrer">
              {bio.aid}
            </a>{' '}
            <strong>Type:</strong> {bio.aidtypeid || 'Literature-derived'}
          </p>

          <p>
            <strong>Data Source:</strong>{' '}
            {bio.aidsrcname ? (
              <a href="https://pubchem.ncbi.nlm.nih.gov/source/ChEMBL" target="_blank" rel="noopener noreferrer">
                {bio.aidsrcname}
              </a>
            ) : 'N/A'}
          </p>

          <p>
            <strong>External ID:</strong>{' '}
            {bio.aidextid ? (
              <a href={`https://www.ebi.ac.uk/chembl/assay_report_card/${bio.aidextid}/`} target="_blank" rel="noopener noreferrer">
                {bio.aidextid}
              </a>
            ) : 'N/A'}
          </p>

          <p>
            <strong>Deposit Date:</strong> {formatDate(bio.depdate)}{' '}
            <strong>Last Modified:</strong> {formatDate(bio.aidmdate)}
          </p>
        </div>
      ))}
    </div>
  );
};

export default BioassayComponent;
